import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { MessageCircle, ThumbsUp, Send, Trash2, X, ChevronDown, ChevronUp } from 'lucide-react';
import { PageTitle } from '../components/PageTitle';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Community = () => {
  const { user } = useAuth();
  const [category, setCategory] = useState('profesionales');
  const [posts, setPosts] = useState([]);
  const [selectedPost, setSelectedPost] = useState(null);
  const [expandedReplies, setExpandedReplies] = useState({}); // Para controlar qué posts tienen respuestas expandidas
  const [replies, setReplies] = useState([]);
  const [newPost, setNewPost] = useState({ title: '', content: '' });
  const [newReply, setNewReply] = useState('');
  const [showNewPost, setShowNewPost] = useState(false);
  const [postingCooldown, setPostingCooldown] = useState(false); // Rate limiting

  useEffect(() => {
    if (['profesional', 'premium', 'usuario_avanzado', 'essential'].includes(user?.subscription_plan)) {
      loadPosts();
    }
  }, [category, user]);

  const loadPosts = async () => {
    try {
      const res = await axios.get(`${API}/community/posts?category=${category}`);
      setPosts(res.data);
    } catch (error) {
      toast.error('Error al cargar posts');
    }
  };

  const handleCreatePost = async () => {
    if (!newPost.title || !newPost.content) {
      toast.error('Completa todos los campos');
      return;
    }
    
    // Rate limiting - evitar spam
    if (postingCooldown) {
      toast.error('Espera 30 segundos antes de crear otro post');
      return;
    }
    
    try {
      await axios.post(`${API}/community/posts`, { ...newPost, category });
      toast.success('Post publicado');
      setNewPost({ title: '', content: '' });
      setShowNewPost(false);
      loadPosts();
      
      // Activar cooldown de 30 segundos
      setPostingCooldown(true);
      setTimeout(() => setPostingCooldown(false), 30000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear post');
    }
  };

  const handleLike = async (postId) => {
    try {
      await axios.post(`${API}/community/posts/${postId}/like`, {});
      loadPosts();
    } catch (error) {
      toast.error('Error');
    }
  };

  const handleDeletePost = async (postId) => {
    try {
      await axios.delete(`${API}/community/posts/${postId}`);
      toast.success('Post eliminado');
      loadPosts();
      if (selectedPost?.post_id === postId) {
        setSelectedPost(null);
      }
    } catch (error) {
      toast.error('Error al eliminar post');
    }
  };

  const loadReplies = async (postId) => {
    try {
      const res = await axios.get(`${API}/community/posts/${postId}/replies`);
      setReplies(res.data);
    } catch (error) {
      toast.error('Error al cargar respuestas');
    }
  };

  const handleReply = async () => {
    if (!newReply) return;
    try {
      await axios.post(`${API}/community/replies`, { post_id: selectedPost.post_id, content: newReply });
      setNewReply('');
      loadReplies(selectedPost.post_id);
      loadPosts();
    } catch (error) {
      toast.error('Error al responder');
    }
  };

  if (!user || !['profesional', 'premium', 'usuario_avanzado', 'essential'].includes(user.subscription_plan)) {
    return (
      <div className="min-h-screen bg-gray-950 p-12">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-5xl font-bold mb-4 text-white">Comunidad E-Home</h1>
          <p className="text-xl text-gray-400 mb-8">Acceso exclusivo para suscriptores</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950 p-6">
      <div className="max-w-6xl mx-auto pt-6">
        <PageTitle>Comunidad E-Home</PageTitle>
        
        {/* Category Tabs - MOBILE: Vertical, DESKTOP: Horizontal */}
        <div className="flex flex-col md:flex-row gap-2 md:gap-4 mb-8">
          {['profesionales', 'vendedores', 'compradores'].map(cat => (
            <button
              key={cat}
              onClick={() => setCategory(cat)}
              className={`px-4 md:px-6 py-3 font-bold uppercase border-4 border-black transition text-sm md:text-base ${
                category === cat
                  ? 'bg-yellow-500 text-black shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]'
                  : 'bg-white text-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* New Post Button - Solo para Admins */}
        {user?.is_admin && (
          <div className="mb-6">
            <button
              onClick={() => setShowNewPost(!showNewPost)}
              className="bg-black text-yellow-500 px-6 py-3 font-bold uppercase border-4 border-black shadow-[6px_6px_0px_0px_rgba(234,179,8,1)] hover:translate-x-1 hover:translate-y-1 transition"
            >
              Nuevo Post (Admin)
            </button>
          </div>
        )}

        {/* New Post Form Modal */}
        {showNewPost && (
          <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
            <div className="bg-white border-4 border-black shadow-[12px_12px_0px_0px_rgba(0,0,0,1)] p-8 max-w-lg w-full">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">Nuevo Post</h2>
                <button onClick={() => setShowNewPost(false)} className="text-2xl">
                  <X />
                </button>
              </div>
              <input
                type="text"
                placeholder="Título del post"
                value={newPost.title}
                onChange={(e) => setNewPost({...newPost, title: e.target.value})}
                className="w-full p-3 border-4 border-black mb-4 text-lg font-bold"
              />
              <textarea
                placeholder="Contenido"
                value={newPost.content}
                onChange={(e) => setNewPost({...newPost, content: e.target.value})}
                className="w-full p-3 border-4 border-black mb-4 h-32"
              />
              <div className="flex gap-4">
                <button onClick={handleCreatePost} className="flex-1 bg-yellow-500 text-black p-3 font-bold border-4 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
                  Publicar
                </button>
                <button onClick={() => setShowNewPost(false)} className="flex-1 bg-gray-200 text-black p-3 font-bold border-4 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Posts List */}
        <div className="grid gap-6">
          {posts.map(post => (
            <div key={post.post_id} className="border-4 border-black p-6 bg-white shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[-2px] hover:translate-y-[-2px] transition">
              <div className="flex items-start gap-4">
                {post.author_picture && (
                  <img src={post.author_picture} alt={post.author_name} className="w-12 h-12 rounded-full border-2 border-black" />
                )}
                <div className="flex-1">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h3 className="text-2xl font-bold">{post.title}</h3>
                      <p className="text-sm text-gray-600">Por {post.author_name}</p>
                    </div>
                    {/* Botón eliminar si es tu post o eres admin */}
                    {(post.user_id === user.user_id || user.is_admin) && (
                      <button
                        onClick={() => handleDeletePost(post.post_id)}
                        className="text-red-500 hover:text-red-700"
                        title="Eliminar post"
                      >
                        <Trash2 size={20} />
                      </button>
                    )}
                  </div>
                  <p className="mb-4">{post.content}</p>
                  <div className="flex gap-4 items-center">
                    <button onClick={() => handleLike(post.post_id)} className="flex items-center gap-2 font-bold hover:text-yellow-600 transition">
                      <ThumbsUp size={20} /> {post.likes}
                    </button>
                    <button 
                      onClick={() => { 
                        if (selectedPost?.post_id === post.post_id) {
                          // Toggle expand/collapse
                          setExpandedReplies(prev => ({...prev, [post.post_id]: !prev[post.post_id]}));
                        } else {
                          setSelectedPost(post); 
                          loadReplies(post.post_id);
                          setExpandedReplies(prev => ({...prev, [post.post_id]: true}));
                        }
                      }}
                      className="flex items-center gap-2 font-bold hover:text-yellow-600 transition"
                    >
                      <MessageCircle size={20} /> {post.replies_count} respuestas
                      {selectedPost?.post_id === post.post_id && (
                        expandedReplies[post.post_id] ? <ChevronUp size={16} /> : <ChevronDown size={16} />
                      )}
                    </button>
                  </div>
                </div>
              </div>

              {/* Replies Section - Minimizable */}
              {selectedPost?.post_id === post.post_id && expandedReplies[post.post_id] && (
                <div className="mt-6 pt-6 border-t-4 border-black">
                  <div className="flex justify-between items-center mb-4">
                    <h4 className="font-bold text-xl">Respuestas ({replies.length})</h4>
                    <button 
                      onClick={() => setExpandedReplies(prev => ({...prev, [post.post_id]: false}))}
                      className="text-sm text-gray-500 hover:text-black flex items-center gap-1"
                    >
                      <ChevronUp size={16} /> Minimizar
                    </button>
                  </div>
                  <div className="space-y-4 mb-4 max-h-80 overflow-y-auto">
                    {replies.length === 0 ? (
                      <p className="text-gray-500 italic">No hay respuestas aún. ¡Sé el primero!</p>
                    ) : (
                      replies.map(reply => (
                        <div key={reply.reply_id} className="pl-6 border-l-4 border-yellow-500">
                          <p className="font-bold">{reply.author_name}</p>
                          <p>{reply.content}</p>
                        </div>
                      ))
                    )}
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder="Escribe una respuesta..."
                      value={newReply}
                      onChange={(e) => setNewReply(e.target.value)}
                      className="flex-1 p-3 border-4 border-black"
                      onKeyPress={(e) => e.key === 'Enter' && handleReply()}
                    />
                    <button onClick={handleReply} className="bg-black text-white px-6 py-3 border-4 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
                      <Send size={20} />
                    </button>
                  </div>
                </div>
              )}
              
              {/* Collapsed replies indicator */}
              {selectedPost?.post_id === post.post_id && !expandedReplies[post.post_id] && (
                <button 
                  onClick={() => setExpandedReplies(prev => ({...prev, [post.post_id]: true}))}
                  className="mt-4 w-full py-2 bg-gray-100 text-gray-600 text-sm font-bold flex items-center justify-center gap-2 hover:bg-gray-200"
                >
                  <ChevronDown size={16} /> Ver {replies.length} respuestas
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
